import type { Memo, MemoEntry, UUID } from "@/lib/types";
import { byPositionThenId } from "@/lib/positionOrder";

export interface OpenTask {
  entryId: UUID;
  /** The to-do text with its leading @-mention chip removed — the assignee is shown as an
   *  avatar beside it, so repeating the name in the text is noise. */
  text: string;
  assigneeId: UUID | null;
  dueDate: string | null;
}

/**
 * 🔴 THE ONE DEFINITION of "an open to-do in this Protokoll".
 *
 * The header pill counts them and the panel lists them, and those two must not be able to
 * disagree — a pill reading "13 offene Aufgaben" over a list of twelve is worse than no pill
 * at all. So the predicate lives here, once, and both read it.
 *
 * A to-do is an entry with an ASSIGNEE (that is what makes it a to-do rather than a note),
 * not done, and not retired. `retired` is the deliberate stop on carry-forward: a retired
 * to-do is one the room decided to let go, and it is not open work.
 *
 * NOTE the scope: this is the to-dos in THIS memo, not the series. The SeriesPage "Aufgaben"
 * tab is a different, wider set (it reads the linked board across every meeting) — which is
 * exactly why the pill must not navigate there. Clicking "13" and landing on a list of forty
 * is a lie about what you clicked.
 */
export function isOpenTask(e: MemoEntry): boolean {
  return !!e.assignee_user_id && !e.task_done && !e.retired;
}

/** Strip a leading `[Name](mention:uuid)` chip. Flow's to-dos are minted from exactly this:
 *  an entry whose FIRST line starts with an @-mention. The name is rendered as an avatar in
 *  the panel, so leaving it in the text would print it twice. Only a LEADING chip goes — a
 *  mention mid-sentence is a reference to a person and part of the sentence. */
export function stripLeadingMention(body: string): string {
  return body.replace(/^\s*\[[^\]]*\]\(mention:[^)]+\)\s*/, "").trim();
}

/** Every open to-do in the memo, in document order (block position, then entry position). */
export function openTasksOf(memo: Memo): OpenTask[] {
  return [...memo.blocks].sort(byPositionThenId).flatMap((block) =>
    [...block.entries]
      .sort(byPositionThenId)
      .filter(isOpenTask)
      .map((e) => ({
        entryId: e.id,
        text: stripLeadingMention(e.body),
        assigneeId: e.assignee_user_id,
        dueDate: e.due_date,
      })),
  );
}
